import React, { useState, useContext, useCallback, useEffect } from "react";
import { FilterContext } from "../FilterContextProvider";
import Select from "react-select";
import Input from "../Components/Input";
import SmallInput from "../Components/SmallInput";
import useApi from "../Api/Api";
import Button from "../Components/Button";
import InventoryLayout from "./InventoryLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTrash } from "@fortawesome/free-solid-svg-icons";
import { NotificationManager } from "react-notifications";
import { UserContext } from "../Auth/UserContextProvider";
import cn from "classnames";
import useSalesSummary from "../hooks/useSalesSummary";
import Footer from "../Components/Footer";

const LocationInventory = () => {
  const { isAdmin } = useContext(UserContext);
  const [resultsLoading, setResultsLoading] = useState(false);
  const { filterData } = useContext(FilterContext);
  const api = useApi();
  const [alterSalesObj, setAlterSalesObj] = useState({});
  const [existingSalesObj, setExistingSalesObj] = useState({});
  const [categories, setCategories] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [unavailableEvents, setUnavailableEvents] = useState([]);
  const [unavailableSalesObj, setUnavailableSalesObj] = useState({});
  const [eventIds, setEventIds] = useState({});
  const [sales, setSales] = useState([]);
  const {
    productsToRender,
    getAllTimeNonShowSales,
    allProdsCategoryFilter,
    setAllProdsCategoryFilter,
    allProdsSearchFilter,
    setAllProdsSearchFilter,
    resetPagination,
  } = useSalesSummary("");

  sales.forEach((sale) => (sale.existing = true));

  const prodsToUse = productsToRender.filter((each) => each.active);

  const { renderFooter, splitItemsList, setCurrentPage } = Footer(prodsToUse);

  let today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();

  today = yyyy + "-" + mm + "-" + dd;
  const [event, setEvent] = useState({
    artist_id: filterData.artist_id,
    date: today,
    reporting_type: "unavailable",
  });

  const categoryOptions = categories.map((category) => ({
    label: category.name,
    value: category.id,
  }));

  const handleCategorySelect = (option) => {
    setCurrentPage(1);
    resetPagination();
    setAllProdsCategoryFilter(option.value);
  };

  const handleCurrentChange = ({ target: { name, value } }, product) => {
    setAlterSalesObj({
      ...alterSalesObj,
      [product.id]: {
        ...product,
        [name]: value,
      },
    });
  };

  const handleUnavailableChange = ({ target: { value } }, product) => {
    setUnavailableSalesObj({
      ...unavailableSalesObj,
      [product.id]: {
        unavailable: value,
        product_id: product.id,
        product_name: product.name,
        category_id: product.category.id,
        product_category: product.category.name,
        size_id: product.size?.id,
        product_size: product.size?.name,
        price: product.retail_price,
        cost: product.cost,
        date: today,
        reporting_type: "unavailable",
        artist_id: filterData.artist_id,
      },
    });
  };

  const handleExistingChange = (
    { target: { value } },
    sale,
    product,
    event
  ) => {
    if (sale?.existing) {
      setExistingSalesObj({
        ...existingSalesObj,
        [sale.id]: {
          existing: true,
          id: sale.id,
          unavailable: value,
        },
      });
    } else {
      setExistingSalesObj({
        ...existingSalesObj,
        [`${product.id}/${event.id}`]: {
          existing: false,
          unavailable: value,
          product_id: product.id,
          product_name: product.name,
          category_id: product.category.id,
          product_category: product.category.name,
          size_id: product.size?.id,
          product_size: product.size?.name,
          price: product.retail_price,
          cost: product.cost,
          date: today,
          reporting_type: "unavailable",
          artist_id: filterData.artist_id,
          event_id: event.id,
        },
      });
    }
  };

  const handleEventChange = ({ target: { id, value } }) => {
    setEvent({
      ...event,
      [id]: value,
    });
  };

  const [searchTimeout, setSearchTimeout] = useState();

  const handleSearchChange = (e) => {
    setSearchTimeout(e.target.value);
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setAllProdsSearchFilter(searchTimeout);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchTimeout, setAllProdsSearchFilter]);

  const resetFilters = () => {
    setCurrentPage(1);
    resetPagination();
    setSearchTimeout();
    setAllProdsCategoryFilter();
  };

  const getUnavailableEvents = useCallback(() => {
    let queryParams = {};
    if (filterData.artist_id) {
      queryParams.artist_id = filterData.artist_id;
    }
    const queryString = Object.keys(queryParams)
      .map((param) => `${param}=${queryParams[param]}`)
      .join("&");
    filterData.artist_id &&
      api
        .get(`/events/all?${queryString}&sort=date&reporting_type=unavailable`)
        .then((r) => {
          setUnavailableEvents(r.data);
        });
  }, [api, filterData]);

  useEffect(() => {
    setResultsLoading(true);
    const queryString = Object.keys(eventIds)
      .map((id) => `event_id[]=${id}`)
      .join("&");
    Object.keys(eventIds).length > 0 &&
      api
        .get(`/sales/all?artist_id=${filterData.artist_id}&${queryString}`)
        .then((r) => {
          setSales(r.data);
          setResultsLoading(false);
        });
  }, [api, filterData.artist_id, eventIds]);

  useEffect(() => {
    const obj = {};
    unavailableEvents.forEach((each) => (obj[each.id] = true));
    setEventIds(obj);
  }, [unavailableEvents]);

  useEffect(() => {
    filterData.artist_id && getUnavailableEvents();
  }, [filterData, getUnavailableEvents]);

  useEffect(() => {
    api.get(`/categories`).then((r) => {
      setCategories(r.data);
    });
  }, [api]);

  const revert = () => {
    const check = window.confirm(
      "Are you sure you want to revert? All unsaved changes will be lost."
    );
    if (check) {
      setAlterSalesObj({});
      setUnavailableSalesObj({});
    }
  };

  const handlePostSales = (e) => {
    e.preventDefault();
    api
      .post(`/events/`, {
        event: {
          ...event,
          sales_attributes: Object.values(unavailableSalesObj).map((sale) => ({
            ...sale,
          })),
        },
      })
      .then(() => {
        NotificationManager.success(
          "Sales successfully posted",
          "Success",
          2000
        );
        getAllTimeNonShowSales();
        setAlterSalesObj({});
        setUnavailableSalesObj({});
        getUnavailableEvents();
        setErrorMessage(false);
        setEvent({
          artist_id: filterData.artist_id,
          date: today,
          reporting_type: "unavailable",
        });
      })
      .catch((error) => {
        setErrorMessage(error.response.data);
      })
      .finally(() => {});
  };

  const handleUpdateSales = (e) => {
    e.preventDefault();
    Promise.all(
      Object.values(existingSalesObj)
        .filter((each) => each.existing)
        .map((sale) => {
          return api.put(`/sales/${sale.id}`, { sale });
        }),
      Object.values(existingSalesObj)
        .filter((each) => !each.existing)
        .map((sale) => {
          return api.post(`/sales/`, { sale });
        })
    )
      .then(() => {
        NotificationManager.success(
          "Sales successfully updated",
          "Success",
          2000
        );
        getAllTimeNonShowSales();
        setExistingSalesObj({});
        getUnavailableEvents();
        setErrorMessage(false);
        setEvent({
          artist_id: filterData.artist_id,
          date: today,
          reporting_type: "unavailable",
        });
      })
      .catch((error) => {
        setErrorMessage(error.response.data);
      })
      .finally(() => {});
  };

  const deleteEvent = (e, eventToDelete) => {
    e.preventDefault();
    const check = window.confirm(
      `Are you sure you want to delete ${eventToDelete.name}?`
    );
    if (check) {
      api.delete(`events/${eventToDelete.id}`).then(() => {
        getUnavailableEvents();
        getAllTimeNonShowSales();
      });
    }
  };

  let count = 0;

  for (let i = 1; i < splitItemsList.length; i++) {
    if (splitItemsList[i - 1].name !== splitItemsList[i].name) {
      count++;
    }
    splitItemsList[i].productNumber = count;
  }

  const renderSelectArtistMessage = () => {
    return (
      <div className="m-8 py-12 pl-4">
        Please select an artist from the filter in order to make an adjustment
      </div>
    );
  };

  const renderAdjustTable = () => {
    return (
      <div>
        {splitItemsList.length > 0 ? (
          <div>
            <form className="flex">
              <table className="w-1/2">
                <thead className="text-xs font-light">
                  <tr className="w-full text-center border-b-2 h-11">
                    <td className="w-1/2 text-left pl-6">PRODUCT</td>
                    <td className="w-1/6">TOTAL CURRENT</td>
                  </tr>
                </thead>
                <tbody>
                  {splitItemsList.map((product, i) => {
                    const prod = alterSalesObj[product.id] || product;
                    return (
                      <tr
                        key={product.id}
                        className={cn("border-b text-sm h-11 text-center", {
                          "bg-yellow-50": unavailableSalesObj[product.id],
                          "bg-gray-100":
                            splitItemsList[i].productNumber % 2 === 1,
                        })}
                      >
                        {product.product_size ? (
                          <td className="text-left pl-6">
                            {product.name} - {product.product_size}
                          </td>
                        ) : (
                          <td className="text-left pl-6">{product.name}</td>
                        )}
                        <td>
                          <SmallInput
                            red_bg
                            name="current"
                            id={`current_${i}`}
                            onChange={(event) =>
                              handleCurrentChange(event, prod)
                            }
                            value={prod.current}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="h-11"></td>
                  </tr>
                </tfoot>
              </table>
              {unavailableEvents.map((event) => {
                return (
                  <table className="w-1/5" key={event.id}>
                    <thead className="text-xs font-light">
                      <tr className="text-center border-b-2 h-11">
                        <td>{event.name}</td>
                      </tr>
                    </thead>
                    <tbody>
                      {splitItemsList.map((product, i) => {
                        const existingSale =
                          existingSalesObj[
                            sales.find(
                              (one) =>
                                one.product_id === product.id &&
                                one.event_id === event.id
                            )?.id
                          ] ||
                          existingSalesObj[`${product.id}/${event.id}`] ||
                          sales.find(
                            (one) =>
                              one.product_id === product.id &&
                              one.event_id === event.id
                          );
                        return (
                          <tr
                            key={product.id}
                            className={cn("border-b text-sm h-11 text-center", {
                              "bg-yellow-50": unavailableSalesObj[product.id],
                              "bg-pink-100":
                                existingSalesObj[
                                  sales.find(
                                    (one) =>
                                      one.product_id === product.id &&
                                      one.event_id === event.id
                                  )?.id
                                ],
                              "bg-gray-100":
                                splitItemsList[i].productNumber % 2 === 1,
                            })}
                          >
                            {/* <td key={product.id}>
                              {sales
                                .filter(
                                  (one) =>
                                    one.product_id === product.id &&
                                    one.event_id === event.id
                                )
                                .map((sale) => {
                                  return sale.unavailable;
                                })}
                            </td> */}
                            <td>
                              <SmallInput
                                name="unavailable"
                                id={`unavailable_${i}`}
                                onChange={(e) =>
                                  handleExistingChange(
                                    e,
                                    existingSale,
                                    product,
                                    event
                                  )
                                }
                                value={existingSale?.unavailable}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="h-11">
                      {isAdmin && (
                        <tr>
                          <td className="text-center">
                            <button onClick={(e) => deleteEvent(e, event)}>
                              <FontAwesomeIcon icon={faTrash}></FontAwesomeIcon>
                            </button>
                          </td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                );
              })}
              <table
                className={
                  errorMessage?.errors?.name ? "w-1/2 -mt-6" : "w-1/2 "
                }
              >
                <thead className="text-xs font-light">
                  {errorMessage?.errors?.name && (
                    <tr className="text-red-500 w-full -mt-5 text-sm">
                      <td className="w-1/3"></td>
                      <td>This field {errorMessage.errors.name}</td>
                    </tr>
                  )}
                  <tr className="w-full text-center border-b-2 h-11">
                    <td className="w-1/3">CURRENTLY AVAILABLE</td>
                    <td className="w-2/3">
                      <Input
                        name="name"
                        placeholder="Reason for unavailabilty"
                        id="event_name"
                        onChange={handleEventChange}
                        value={event.name}
                      />
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {splitItemsList.map((product, i) => {
                    const prod = alterSalesObj[product.id] || product;
                    return (
                      <tr
                        key={product.id}
                        className={cn("border-b text-sm h-11 text-center", {
                          "bg-yellow-50": unavailableSalesObj[product.id],
                          "bg-gray-100":
                            splitItemsList[i].productNumber % 2 === 1,
                        })}
                      >
                        <td>
                          {unavailableSalesObj[product.id]?.unavailable
                            ? prod.currently_available -
                              unavailableSalesObj[product.id]?.unavailable
                            : prod.currently_available}
                        </td>
                        <td>
                          <SmallInput
                            name="unavailable"
                            id={`unavailable_${i}`}
                            onChange={(e) =>
                              handleUnavailableChange(e, product)
                            }
                            value={unavailableSalesObj[product.id]?.unavailable}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="h-11"></td>
                  </tr>
                </tfoot>
              </table>
            </form>
            {Object.keys(unavailableSalesObj).length > 0 && (
              <span className="flex justify-center my-6">
                <span className="mr-2">
                  <Button
                    className="button button-white"
                    onClick={revert}
                    text="Revert"
                  ></Button>
                </span>
                <Button
                  className="button"
                  onClick={handlePostSales}
                  text="Post"
                ></Button>
              </span>
            )}
            {Object.keys(existingSalesObj).length > 0 && (
              <span className="flex justify-center my-6">
                <span className="mr-2">
                  <Button
                    className="button button-white"
                    onClick={revert}
                    text="Revert"
                  ></Button>
                </span>
                <Button
                  className="button"
                  onClick={handleUpdateSales}
                  text="Update"
                ></Button>
              </span>
            )}
            {errorMessage?.error && (
              <div className="text-red-500 flex justify-center my-4">
                {errorMessage.error}
              </div>
            )}
          </div>
        ) : (
          <div className="mx-12 pb-16">
            {resultsLoading ? (
              <>Results are loading...</>
            ) : (
              <> There is no sales history over this date range</>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <InventoryLayout>
      <div className="text-black bg-gray-100 pt-2">
        <div className="mt-8 mx-8 text-black bg-white rounded-lg border">
          <div className="mx-10 pt-8 pb-10 font-medium text-sm text-black">
            <div className="text-black text-lg mb-4">
              Inventory Unavailability
            </div>
            <div className="flex text-sm"></div>
            <div className="flex">
              <div className="absolute mt-2 ml-2 text-black">
                <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
              </div>
              <div className="w-1/4">
                <Input
                  onChange={handleSearchChange}
                  value={searchTimeout}
                  placeholder="Search for products"
                  bigPaddingLeft
                />
              </div>
              <Select
                id="category_filter"
                className="text-xs font-light w-1/4 ml-2"
                placeholder="Filter by category"
                options={categoryOptions}
                onChange={handleCategorySelect}
                value={categoryOptions?.filter(
                  (each) => each.value === allProdsCategoryFilter
                )}
              />
              {(allProdsSearchFilter || allProdsCategoryFilter) && (
                <button
                  onClick={resetFilters}
                  className="text-blue-500 font-normal italic text-sm ml-2 pl-2"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
          {filterData.artist_id
            ? renderAdjustTable()
            : renderSelectArtistMessage()}
        </div>
        {splitItemsList.length > 0 && <>{renderFooter()}</>}
      </div>
    </InventoryLayout>
  );
};

export default LocationInventory;
